import { Component, ChangeDetectorRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonModule } from "@angular/common";
import { FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";
import {
  PromptBundleTreeItem,
  PromptItem,
} from "../../../core/models/prompt-manager";
import { RestService } from "../../../core/services/rest.service";

export interface PromptFilterState {
  service: string;
  scope: "global" | "provider" | "model" | "";
  provider: string;
  model: string;
  promptKey: string;
  version: string;
  cachedPrompts: PromptItem[];
  bundleId?: string | null;
}

export interface PromptFilterResult {
  service: string;
  scope: "global" | "provider" | "model" | "";
  provider: string;
  model: string;
  promptKey: string;
  version: string;
  /** Resolved bundle_id for the selected service / scope / provider / model. */
  bundleId: string | null;
  /** Prompts fetched for the current bundle selection, filtered on apply. */
  prompts: PromptItem[];
  /** Full unfiltered prompt list for the selected bundle (for filter restore). */
  cachedPrompts: PromptItem[];
  /** True when the user clicked Reset all before applying. */
  reset?: boolean;
}

export type PromptFilterModalMode = "prompt" | "tcg";

export interface TcgFilterState {
  userStory: string;
  priority: string;
}

export interface TcgFilterResult {
  userStory: string;
  priority: string;
  reset?: boolean;
}

@Component({
  selector: "app-prompt-filter-modal",
  standalone: true,
  imports: [CommonModule, DynamicFormComponent],
  templateUrl: "./prompt-filter-modal.component.html",
  styleUrl: "./prompt-filter-modal.component.scss",
})
export class PromptFilterModalComponent implements OnInit, OnDestroy {
  activeModal = inject(NgbActiveModal);
  @Input() data: any = "";
  /** `prompt` (default) keeps the existing prompt-templates filter UI unchanged. */
  @Input() mode: PromptFilterModalMode = "prompt";
  /** Previously applied filter values — restored when the modal reopens. */
  @Input() initialFilter: PromptFilterState | null = null;
  /** TCG mode: story options for the User Story step. */
  @Input() tcgUserStories: { label: string; value: string }[] = [];
  /** TCG mode: previously applied filter — restored when the modal reopens. */
  @Input() initialTcgFilter: TcgFilterState | null = null;
  /** TCG mode: when false, only User Story is shown (e.g. test case detail filter). */
  @Input() tcgShowPriority = true;
  /** TCG mode: when false, hides User Story (e.g. BPD-sourced test cases). */
  @Input() tcgShowUserStory = true;
  /** Notifies the parent to clear persisted filter state on Reset all. */
  @Input() onFilterReset?: () => void;

  @ViewChild('filterContent') filterContent!: ElementRef<HTMLElement>;

  isActive = false;
  isLoadingTree = false;
  isLoadingPrompts = false;
  /** TCG mode: drives Apply button enabled state. */
  tcgApplyEnabled = false;

  dynamicForm: FormGroup = new FormGroup({});
  currentStep = 1;
  maxReachedStep = 1;

  private bundleTree: PromptBundleTreeItem[] = [];
  private cachedPrompts: PromptItem[] = [];
  private wasReset = false;
  private formSub?: Subscription;
  /** TCG mode: last field the user interacted with on the right panel. */
  private tcgLastFocusedFieldKey: string | null = null;

  sidebarItems = [
    { key: "service", label: "Service" },
    { key: "scope", label: "Scope" },
    { key: "provider", label: "Provider" },
    { key: "model", label: "Model" },
    { key: "promptKey", label: "Prompt Key" }
  ];

  formFields: any[] = [
    {
      name: "Service",
      key: "service",
      type: "select",
      placeholder: "Select service",
      options: [],
      validations: { required: false },
    },
    {
      name: "Scope",
      key: "scope",
      type: "label-card",
      options: [
        { label: "Global", value: "global" },
        { label: "Provider", value: "provider" },
        { label: "Model", value: "model" },
      ],
      validations: { required: false },
    },
    {
      name: "Provider",
      key: "provider",
      type: "select",
      placeholder: "Select provider",
      options: [],
      disabled: true,
      validations: { required: false },
    },
    {
      name: "Model",
      key: "model",
      type: "select",
      placeholder: "Select model",
      options: [],
      disabled: true,
      validations: { required: false },
    },
    {
      name: "Prompt Key",
      key: "promptKey",
      type: "select",
      placeholder: "Select prompt key",
      options: [],
      disabled: true,
      validations: { required: false },
    }
  ];

  constructor(
    private readonly rest: RestService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.formSub = this.dynamicForm.valueChanges.subscribe(() => {
      if (this.mode === "tcg") {
        this.updateTcgSidebarFocus();
        this.refreshTcgApplyEnabled();
        this.syncControlStates();
        this.cdr.detectChanges();
        return;
      }
      this.updateCurrentStep();
      this.syncControlStates();
    });
    if (this.mode === "tcg") {
      this.initTcgMode();
    } else {
      this.loadTree();
    }
  }

  private initTcgMode(): void {
    this.currentStep = 0;
    this.tcgLastFocusedFieldKey = null;
    this.sidebarItems = [];
    if (this.tcgShowUserStory) {
      this.sidebarItems.push({ key: "userStory", label: "User Story" });
    }
    if (this.tcgShowPriority) {
      this.sidebarItems.push({ key: "priority", label: "Priority" });
    }
    this.formFields = this.buildTcgFormFields();
    this.prepareTcgFilterRestore();
    setTimeout(() => {
      if (this.initialTcgFilter) {
        this.completeTcgFilterRestore();
      } else {
        this.syncControlStates();
        this.updateTcgSidebarFocus();
        this.refreshTcgApplyEnabled();
        this.cdr.detectChanges();
      }
    });
  }

  private buildTcgFormFields(): any[] {
    const fields: any[] = [];

    if (this.tcgShowUserStory) {
      fields.push({
        name: "User Story",
        key: "userStory",
        type: "select",
        placeholder: "Select User Story",
        options: this.tcgUserStories,
        validations: { required: false },
      });
    }

    if (this.tcgShowPriority) {
      fields.push({
        name: "Priority",
        key: "priority",
        type: "label-card",
        options: [
          { label: "Critical", value: "P1" },
          { label: "High", value: "P2" },
          // { label: "Medium", value: "P3" },
          // { label: "Low", value: "P4" },
        ],
        validations: { required: false },
      });
    }

    return fields;
  }

  private prepareTcgFilterRestore(): void {
    const f = this.initialTcgFilter;
    if (!f) return;

    this.formFields.forEach((field) => {
      const value = f[field.key as keyof TcgFilterState];
      if (value) field.value = value;
    });
    this.formFields = [...this.formFields];
  }

  private completeTcgFilterRestore(): void {
    const f = this.initialTcgFilter;
    if (!f) return;

    this.maxReachedStep = Math.max(0, this.formFields.length - 1);
    const patch: Record<string, string> = {};
    if (this.tcgShowUserStory) {
      patch["userStory"] = f.userStory ?? "";
    }
    if (this.tcgShowPriority) {
      patch["priority"] = f.priority ?? "";
    }
    this.dynamicForm.patchValue(patch, { emitEvent: false });
    if (this.tcgShowPriority && f.priority) {
      this.tcgLastFocusedFieldKey = "priority";
    } else if (this.tcgShowUserStory && f.userStory) {
      this.tcgLastFocusedFieldKey = "userStory";
    }
    this.formFields = [...this.formFields];
    setTimeout(() => {
      this.updateTcgSidebarFocus();
      this.refreshTcgApplyEnabled();
      this.syncControlStates();
      this.cdr.detectChanges();
    });
  }

  /** TCG mode: sidebar focus follows the last field selected on the right. */
  private updateTcgSidebarFocus(changedFieldKey?: string): void {
    if (changedFieldKey) {
      this.tcgLastFocusedFieldKey = changedFieldKey;
    }

    if (this.tcgLastFocusedFieldKey) {
      const idx = this.formFields.findIndex((f) => f.key === this.tcgLastFocusedFieldKey);
      if (idx >= 0) {
        this.currentStep = idx;
        return;
      }
    }

    const values = this.dynamicForm.getRawValue();
    if (values.priority) {
      const priorityIdx = this.formFields.findIndex((f) => f.key === "priority");
      if (priorityIdx >= 0) {
        this.currentStep = priorityIdx;
        return;
      }
    }
    if (values.userStory) {
      this.currentStep = 0;
      return;
    }

    this.currentStep = 0;
  }

  private refreshTcgApplyEnabled(): void {
    const values = this.dynamicForm.getRawValue();
    const hasUserStory = this.tcgShowUserStory && !!values.userStory;
    const hasPriority = this.tcgShowPriority && !!values.priority;
    this.tcgApplyEnabled = !!(hasUserStory || hasPriority);
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
  }

  // ── Tree loading ───────────────────────────────────────────────────────────

  private setFieldLoading(key: string, loading: boolean): void {
    const field = this.formFields.find((f) => f.key === key);
    if (field) {
      field.isLoading = loading;
      this.formFields = [...this.formFields];
    }
  }

  private loadTree(): void {
    this.isLoadingTree = true;
    this.setFieldLoading("service", true);
    this.rest.getPromptBundleTree().subscribe({
      next: (response) => {
        this.bundleTree = this.normalizeBundleTreeResponse(response);
        this.refreshServiceOptions();
        this.prepareFilterRestore();
        this.isLoadingTree = false;
        this.setFieldLoading("service", false);
        setTimeout(() => {
          if (this.initialFilter) {
            this.completeFilterRestore();
          } else {
            this.refreshStepAndControlStates();
          }
        });
      },
      error: () => {
        this.isLoadingTree = false;
        this.setFieldLoading("service", false);
      },
    });
  }

  private normalizeBundleTreeResponse(response: unknown): PromptBundleTreeItem[] {
    if (Array.isArray(response)) return response;
    if (response && typeof response === "object") {
      const wrapped = response as { data?: unknown; services?: unknown };
      if (Array.isArray(wrapped.data)) return wrapped.data as PromptBundleTreeItem[];
      if (Array.isArray(wrapped.services)) return wrapped.services as PromptBundleTreeItem[];
    }
    return [];
  }

  private refreshServiceOptions(): void {
    const field = this.formFields.find((f) => f.key === "service");
    if (field) {
      field.options = this.bundleTree.map((s) => ({
        label: s.service,
        value: s.service,
      }));
      this.formFields = [...this.formFields]; // new ref triggers DynamicFormComponent.ngOnChanges
    }
  }

  // ── Cascade handlers wired from (modelChange) ──────────────────────────────

  onFieldChange(event: { field: any; value: any }): void {
    if (this.mode === "tcg") {
      if (event.value) {
        this.wasReset = false;
      }
      this.updateTcgSidebarFocus(event.field.key);
      this.refreshTcgApplyEnabled();
      this.cdr.detectChanges();
      return;
    }

    if (event.field.key === "service") {
      this.onServiceSelected(event.value);
    } else if (event.field.key === "scope") {
      this.onScopeSelected(event.value);
    } else if (event.field.key === "provider") {
      this.onProviderSelected(event.value);
    } else if (event.field.key === "model") {
      this.onModelSelected(event.value);
    }
  }

  private onServiceSelected(serviceName: string, restoring = false): void {
    if (!restoring) {
      this.dynamicForm.get("provider")?.setValue("", { emitEvent: false });
      this.dynamicForm.get("model")?.setValue("", { emitEvent: false });
      this.clearPromptKeySelection();
    }

    const serviceNode = this.bundleTree.find((s) => s.service === serviceName);

    const providerField = this.formFields.find((f) => f.key === "provider");
    if (providerField) {
      providerField.options = (serviceNode?.providers ?? []).map((p) => ({
        label: p.provider,
        value: p.provider,
      }));
      providerField.disabled = !serviceName;
    }

    const modelField = this.formFields.find((f) => f.key === "model");
    if (modelField) {
      modelField.options = [];
      modelField.disabled = true;
    }

    this.formFields = [...this.formFields];
    if (!restoring) {
      this.refreshStepAndControlStates();
    }
  }

  private onScopeSelected(
    scope: string,
    restoring = false,
    serviceOverride?: string
  ): void {
    if (!restoring) {
      this.dynamicForm.get("provider")?.setValue("", { emitEvent: false });
      this.dynamicForm.get("model")?.setValue("", { emitEvent: false });
      this.clearPromptKeySelection();
    }

    const providerField = this.formFields.find((f) => f.key === "provider");
    const modelField = this.formFields.find((f) => f.key === "model");
    const serviceName =
      serviceOverride ?? this.dynamicForm.get("service")?.value;

    if (scope === "global") {
      if (providerField) {
        providerField.options = [];
        providerField.disabled = true;
      }
      if (modelField) {
        modelField.options = [];
        modelField.disabled = true;
      }
    } else if (serviceName) {
      this.onServiceSelected(serviceName, true);
      if (modelField) {
        modelField.disabled = scope !== "model";
      }
    }

    this.formFields = [...this.formFields];
    if (!restoring) {
      this.refreshStepAndControlStates();
      if (scope === "global") {
        this.loadPromptsForSelection();
      }
    }
  }

  private onProviderSelected(
    providerName: string,
    restoring = false,
    serviceOverride?: string
  ): void {
    if (!restoring) {
      this.dynamicForm.get("model")?.setValue("", { emitEvent: false });
      this.clearPromptKeySelection();
    }

    const serviceName =
      serviceOverride ?? this.dynamicForm.get("service")?.value;
    const serviceNode = this.bundleTree.find((s) => s.service === serviceName);
    const providerNode = serviceNode?.providers.find(
      (p) => p.provider === providerName
    );

    const modelField = this.formFields.find((f) => f.key === "model");
    if (modelField) {
      modelField.options = (providerNode?.models ?? []).map((m) => ({
        label: m.model,
        value: m.model,
      }));
      modelField.disabled = !providerName;
    }

    this.formFields = [...this.formFields];
    if (!restoring) {
      this.refreshStepAndControlStates();
      const scope = this.dynamicForm.get("scope")?.value;
      if (scope === "provider" && providerName) {
        this.loadPromptsForSelection();
      }
    }
  }

  private onModelSelected(modelName: string): void {
    this.clearPromptKeySelection();
    if (!modelName) return;
    this.loadPromptsForSelection();
  }

  private loadPromptsForSelection(): void {
    const bundleId = this.resolveBundleId(this.dynamicForm.getRawValue());
    if (!bundleId) {
      this.clearPromptKeySelection();
      return;
    }

    this.isLoadingPrompts = true;
    this.setFieldLoading("promptKey", true);
    this.rest.getPromptsByBundle(bundleId).subscribe({
      next: (data) => {
        this.cachedPrompts = data ?? [];
        this.refreshPromptKeyOptions(this.cachedPrompts);
        this.isLoadingPrompts = false;
        this.setFieldLoading("promptKey", false);
        this.refreshStepAndControlStates();
      },
      error: () => {
        this.cachedPrompts = [];
        this.refreshPromptKeyOptions([]);
        this.isLoadingPrompts = false;
        this.setFieldLoading("promptKey", false);
        this.refreshStepAndControlStates();
      },
    });
  }

  private clearPromptKeySelection(): void {
    this.cachedPrompts = [];
    this.dynamicForm.get("promptKey")?.setValue("", { emitEvent: false });
    this.refreshPromptKeyOptions([]);
  }

  private refreshPromptKeyOptions(prompts: PromptItem[]): void {
    const keys = [...new Set(prompts.map((p) => p.key))].sort();
    const promptKeyField = this.formFields.find((f) => f.key === "promptKey");
    if (promptKeyField) {
      promptKeyField.options = keys.map((key) => ({ label: key, value: key }));
      promptKeyField.disabled = keys.length === 0;
    }
    this.formFields = [...this.formFields];
  }

  /** Seeds field defaults and cascade options before the dynamic form mounts. */
  private prepareFilterRestore(): void {
    const f = this.initialFilter;
    if (!f) return;

    this.cachedPrompts = f.cachedPrompts ?? [];

    if (f.service) this.onServiceSelected(f.service, true);
    if (f.scope) this.onScopeSelected(f.scope, true, f.service);
    if (f.provider) this.onProviderSelected(f.provider, true, f.service);
    if (this.cachedPrompts.length) this.refreshPromptKeyOptions(this.cachedPrompts);

    const values: Record<string, string> = {
      service: f.service,
      scope: f.scope,
      provider: f.provider,
      model: f.model,
      promptKey: f.promptKey,
      version: f.version ?? "",
    };

    this.formFields.forEach((field) => {
      const value = values[field.key];
      if (value) field.value = value;
    });
    this.formFields = [...this.formFields];
  }

  /** Patches controls after app-dynamic-form has built the form group. */
  private completeFilterRestore(): void {
    const f = this.initialFilter;
    if (!f) return;

    this.maxReachedStep = this.formFields.length - 1;

    this.dynamicForm.patchValue(
      {
        service: f.service,
        scope: f.scope,
        provider: f.provider,
        model: f.model,
        promptKey: f.promptKey,
        version: f.version ?? "",
      },
      { emitEvent: false }
    );

    // Rebuild selects so options + values render together (fixes provider on reopen).
    this.formFields = [...this.formFields];

    setTimeout(() => {
      this.dynamicForm.patchValue(
        {
          service: f.service,
          scope: f.scope,
          provider: f.provider,
          model: f.model,
          promptKey: f.promptKey,
          version: f.version ?? "",
        },
        { emitEvent: false }
      );
      this.currentStep = this.findFirstUnfilledFieldIndex();
      this.syncControlStates();
      this.cdr.detectChanges();
    });
  }

  private refreshStepAndControlStates(): void {
    this.updateCurrentStep();
    this.syncControlStates();
    this.cdr.detectChanges();
  }

  /** Returns whether a cascade field should be disabled based on current selections. */
  private isCascadeFieldDisabled(fieldKey: string, field: any): boolean {
    if (this.mode === "tcg") {
      return false;
    }

    const service = this.dynamicForm.get("service")?.value;
    const scope = this.dynamicForm.get("scope")?.value;
    const provider = this.dynamicForm.get("provider")?.value;

    if (fieldKey === "provider") {
      return (
        !service ||
        !scope ||
        scope === "global" ||
        !(field.options?.length)
      );
    }
    if (fieldKey === "model") {
      return scope !== "model" || !provider || !(field.options?.length);
    }
    if (fieldKey === "promptKey") {
      return !(field.options?.length);
    }
    return false;
  }

  private syncControlStates(): void {
    if (this.mode === "tcg") {
      this.formFields.forEach((field) => {
        const control = this.dynamicForm.get(field.key);
        if (!control) return;
        field.disabled = false;
        control.enable({ emitEvent: false });
      });
      return;
    }

    this.formFields.forEach((field, index) => {
      const control = this.dynamicForm.get(field.key);
      if (!control) return;

      const stepUnlocked = index <= this.maxReachedStep;
      const cascadeDisabled = this.isCascadeFieldDisabled(field.key, field);
      const shouldDisable = !stepUnlocked || cascadeDisabled;

      field.disabled = shouldDisable;
      if (shouldDisable) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  // ── Sidebar navigation ─────────────────────────────────────────────────────

  updateCurrentStep(): void {
    this.currentStep = this.findFirstUnfilledFieldIndex();
    this.maxReachedStep = Math.max(
      this.maxReachedStep,
      this.calculateMaxReachedStep()
    );
  }

  private findFirstUnfilledFieldIndex(): number {
    for (let i = 0; i < this.formFields.length; i++) {
      const value = this.dynamicForm.get(this.formFields[i].key)?.value;
      if (!value || value === "") return i;
    }
    return this.formFields.length - 1;
  }

  private calculateMaxReachedStep(): number {
    const service = this.dynamicForm.get("service")?.value;
    const scope = this.dynamicForm.get("scope")?.value;

    if (service && scope) return this.getMaxStepWhenBothFilled();
    if (service || scope)
      return Math.min(this.currentStep + 1, this.formFields.length - 1);
    return 1;
  }

  private getMaxStepWhenBothFilled(): number {
    let maxStep = Math.min(this.currentStep + 1, this.formFields.length - 1);
    for (let i = 2; i < this.formFields.length; i++) {
      const value = this.dynamicForm.get(this.formFields[i].key)?.value;
      if (value && value !== "") {
        maxStep = Math.max(maxStep, Math.min(i + 1, this.formFields.length - 1));
      } else {
        break;
      }
    }
    return maxStep;
  }

  updateFieldStates(): void {
    this.syncControlStates();
  }

  isSectionActive(index: number): boolean {
    return index === this.currentStep;
  }

  isSectionCompleted(index: number): boolean {
    const value = this.dynamicForm.get(this.formFields[index]?.key)?.value;
    return !!(value && value !== "");
  }

  isSectionEnabled(index: number): boolean {
    if (this.mode === "tcg") {
      return true;
    }
    return index <= this.maxReachedStep;
  }

  onSidebarNavClick(index: number): void {
    if (this.mode === "tcg") {
      return;
    }
    this.selectSection(index);
  }

  selectSection(index: number): void {
    if (index <= this.maxReachedStep) {
      this.currentStep = index;
      this.filterContent?.nativeElement?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  resetAll(): void {
    this.wasReset = true;
    this.onFilterReset?.();

    if (this.mode === "tcg") {
      this.initialTcgFilter = null;
      this.tcgLastFocusedFieldKey = null;
      this.currentStep = 0;
      this.maxReachedStep = 1;

      this.formFields.forEach((field) => {
        delete field.value;
      });

      this.formFields.forEach((field) => {
        const control = this.dynamicForm.get(field.key);
        control?.setValue("", { emitEvent: false });
        control?.enable({ emitEvent: false });
      });
      this.dynamicForm.patchValue(
        { userStory: "", priority: "" },
        { emitEvent: false }
      );

      this.syncControlStates();
      this.updateTcgSidebarFocus();
      this.refreshTcgApplyEnabled();
      this.cdr.detectChanges();
      return;
    }

    this.dynamicForm.reset();
    this.currentStep = 0;
    this.maxReachedStep = 1;

    this.formFields.forEach((field) => {
      delete field.value;
    });

    this.initialFilter = null;
    this.cachedPrompts = [];

    const providerField = this.formFields.find((f) => f.key === "provider");
    const modelField = this.formFields.find((f) => f.key === "model");
    const promptKeyField = this.formFields.find((f) => f.key === "promptKey");
    if (providerField) { providerField.options = []; providerField.disabled = true; }
    if (modelField) { modelField.options = []; modelField.disabled = true; }
    if (promptKeyField) { promptKeyField.options = []; promptKeyField.disabled = true; }
    this.formFields = [...this.formFields];
    this.refreshStepAndControlStates();
  }

  applyFilters(): void {
    if (this.mode === "tcg") {
      const values = this.dynamicForm.getRawValue();
      const result: TcgFilterResult = {
        userStory: this.tcgShowUserStory ? (values.userStory ?? "") : "",
        priority: this.tcgShowPriority ? (values.priority ?? "") : "",
        reset: this.wasReset,
      };
      this.activeModal.close(result);
      return;
    }

    const values = this.dynamicForm.getRawValue();
    let filteredPrompts = [...this.cachedPrompts];

    if (values.promptKey) {
      filteredPrompts = filteredPrompts.filter((p) => p.key === values.promptKey);
    }

    if (values.version) {
      const ver = values.version.toLowerCase();
      filteredPrompts = filteredPrompts.filter(
        (p) =>
          p.active_version.toLowerCase().includes(ver) ||
          p.versions.some((v) => v.version.toLowerCase().includes(ver))
      );
    }

    const result: PromptFilterResult = {
      service: values.service ?? "",
      scope: values.scope ?? "",
      provider: values.provider ?? "",
      model: values.model ?? "",
      promptKey: values.promptKey ?? "",
      version: values.version ?? "",
      bundleId: this.resolveBundleId(values),
      prompts: filteredPrompts,
      cachedPrompts: this.cachedPrompts,
      reset: this.wasReset,
    };
    this.activeModal.close(result);
  }

  /**
   * Walks the locally-stored bundle tree to find the correct bundle_id for
   * the selected service / scope / provider / model combination.
   */
  private resolveBundleId(values: any): string | null {
    const { service, scope, provider, model } = values;
    if (!service) return null;

    const serviceNode = this.bundleTree.find((s) => s.service === service);
    if (!serviceNode) return null;

    if (!scope) return null;

    if (scope === "global") {
      const bundleId = serviceNode.global_bundle?.bundle_id;
      return bundleId || null;
    }

    if (scope === "provider") {
      if (!provider) return null;
      const provNode = serviceNode.providers.find((p) => p.provider === provider);
      const bundleId = provNode?.global_bundle?.bundle_id;
      return bundleId || null;
    }

    if (scope === "model") {
      if (!provider || !model) return null;
      const provNode = serviceNode.providers.find((p) => p.provider === provider);
      const modelNode = provNode?.models.find((m) => m.model === model);
      const bundleId = modelNode?.bundle_id;
      return bundleId || null;
    }

    return null;
  }
}
